import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

const CallbackConfigurationForm = ({ configId, initialData, onSaveSuccess, onClose, mode = 'edit', eventName, eventId }) => {
  const [formData, setFormData] = useState({
    event_name: '',
    webhook_server: [], // Changed from webhook_url to webhook_server array
    version: '1',
    description: '',
    payload_template: '{}',
    message_template: '',
    is_active: true,
    priority: 100,
    health_system: '',
    provider: '',
    department: '',
    clinic: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventError, setEventError] = useState(null);
  const [defaultDataGenerated, setDefaultDataGenerated] = useState(false);

  const [healthSystems, setHealthSystems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [clinics, setClinics] = useState([]);
  // Webhook servers state
  const [webhookServers, setWebhookServers] = useState([]);
  const [webhookServersLoading, setWebhookServersLoading] = useState(true);
// Fetch webhook servers (including settings.py fallback)
const fetchWebhookServers = async () => {
  try {
    setWebhookServersLoading(true);
    // Fetch both database servers and settings.py fallback
    const [serversResponse, settingsResponse] = await Promise.all([
      adminApi.get('/admin/callback/system/core/manage/servers/'),
      adminApi.get('/admin/callback/system/core/')
    ]);
    let servers = [];
    // Add database webhook servers
    if (serversResponse.data && Array.isArray(serversResponse.data)) {
      servers = serversResponse.data.filter(server => server.is_active).map(server => ({
        ...server,
        source: 'database'
      }));
    }
    // Add fallback from settings.py if no database servers exist or if settings.py has data
    if (settingsResponse.data && settingsResponse.data.success) {
      const hasSettingsData = settingsResponse.data.CORE_SERVER_WEBHOOK_URL || settingsResponse.data.CORE_SERVER_WEBHOOK_SECRET_CONFIGURED;
      if (hasSettingsData && servers.length === 0) {
        // Only add settings.py data if no database servers exist
        servers.push({
          id: 'settings-py',
          core_server_webhook_url: settingsResponse.data.CORE_SERVER_WEBHOOK_URL || '',
          core_server_webhook_secret: settingsResponse.data.CORE_SERVER_WEBHOOK_SECRET_CONFIGURED ? '••••••••' : '',
          description: 'From settings.py (Legacy)',
          is_active: true,
          source: 'settings_py',
          created_at: null,
          updated_at: null
        });
      }
    }
    setWebhookServers(servers);
    // Select all servers by default as requested
    if (servers.length > 0 && formData.webhook_server.length === 0) {
      setFormData(prevData => ({
        ...prevData,
        webhook_server: servers.map(server => server.id)
      }));
    }
  } catch (err) {
    console.error('Error fetching webhook servers:', err);
  } finally {
    setWebhookServersLoading(false);
  }
};

  // Fetch configuration data when configId is provided
  useEffect(() => {
    const fetchConfigurationData = async () => {
      if (configId && !initialData) {
        try {
          setLoading(true);
          setError(null);
          const response = await adminApi.get(`/admin/callback/system/core/manage/configurations/${configId}/`);
          if (response.data && response.data.success) {
            const configData = response.data.data;
            setFormData(configData);
            // Set selected event if config has event_name and events are loaded
            if (configData.event_name && events.length > 0) {
              const event = events.find(e => e.event_name === configData.event_name);
              setSelectedEvent(event);
            }
          } else {
            setError(response.data?.message || 'Failed to fetch configuration data.');
          }
        } catch (err) {
          setError(err.message || 'An error occurred while fetching configuration data.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchConfigurationData();
  }, [configId, initialData, events]);

  // Initialize form data from initialData prop or default values
  useEffect(() => {
    const initializeFormData = async () => {
      if (initialData) {
        // Handle webhook_server array from initialData
        let webhookServerIds = [];
        if (initialData.webhook_server && Array.isArray(initialData.webhook_server)) {
          webhookServerIds = initialData.webhook_server;
        } else if (initialData.webhook_url && webhookServers.length > 0) {
          // Legacy: convert webhook_url to webhook_server array
          const matchingServer = webhookServers.find(server =>
            server.core_server_webhook_url === initialData.webhook_url
          );
          if (matchingServer) {
            webhookServerIds = [matchingServer.id];
          }
        }
        
        const updatedData = {
          ...initialData,
          webhook_server: webhookServerIds.length > 0 ? webhookServerIds : webhookServers.map(s => s.id)
        };
        setFormData(updatedData);
        
        // Set selected event if initialData has event_name
        if (initialData.event_name && events.length > 0) {
          const event = events.find(e => e.event_name === initialData.event_name);
          setSelectedEvent(event);
        }
      } else {
        // For new configurations, select all webhook servers by default
        const newFormData = {
          ...formData,
          webhook_server: webhookServers.map(server => server.id),
          ...(eventName && { event_name: eventName })
        };
        setFormData(newFormData);
        
        // Set selected event if eventName is provided and events are loaded
        if (eventName && events.length > 0) {
          const event = events.find(e => e.event_name === eventName);
          setSelectedEvent(event);
        }
      }
    };
    
    // Only initialize when we have webhook servers loaded
    if (!webhookServersLoading && webhookServers.length >= 0) {
      initializeFormData();
    }
  }, [initialData, events, eventName, webhookServers, webhookServersLoading]);
  // Fetch webhook servers
  useEffect(() => {
    fetchWebhookServers();
  }, []);
  // Fetch dynamic events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setEventLoading(true);
        const response = await adminApi.get('/admin/callback/system/core/events/');
        if (response.data && response.data.success) {
          setEvents(response.data.data.events);
          setEventError(null);
        } else {
          setEventError(response.data?.message || 'Failed to fetch events.');
        }
      } catch (err) {
        setEventError(err.message || 'An error occurred while fetching events.');
      } finally {
        setEventLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Fetch organizational data
  useEffect(() => {
    const fetchOrgData = async () => {
      try {
        const [hsRes, provRes] = await Promise.all([
          adminApi.get('/admin/health-systems/'),
          adminApi.get('/admin/providers/'),
        ]);
        if (hsRes.data && hsRes.data.success) {
          setHealthSystems(hsRes.data.health_systems);
        }
        if (provRes.data && provRes.data.success) {
          setProviders(provRes.data.providers);
        }
      } catch (err) {
        setError(err.message || 'Error fetching organizational data.');
      }
    };
    fetchOrgData();
  }, []);

  // Fetch departments and clinics based on selected health system
  useEffect(() => {
    if (formData.health_system) {
      const fetchDepsAndClinics = async () => {
        try {
          const [depRes, clinicRes] = await Promise.all([
            adminApi.get(`/admin/health-system/${formData.health_system}/departments/`),
            adminApi.get(`/admin/health-system/${formData.health_system}/clinics/`),
          ]);
          if (depRes.data && depRes.data.success) {
            setDepartments(depRes.data.departments);
          }
          if (clinicRes.data && clinicRes.data.success) {
            setClinics(clinicRes.data.clinics);
          }
        } catch (err) {
          setError(err.message || 'Error fetching departments/clinics.');
        }
      };
      fetchDepsAndClinics();
    } else {
      setDepartments([]);
      setClinics([]);
    }
  }, [formData.health_system]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEventChange = (e) => {
    const selectedEventName = e.target.value;
    const event = events.find(ev => ev.event_name === selectedEventName);
    setFormData(prevData => ({ ...prevData, event_name: selectedEventName }));
    setSelectedEvent(event);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Filter out non-UUID webhook server IDs (like "settings-py") before sending to backend
      const isValidUUID = (str) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(str);
      };
      const filteredWebhookServers = formData.webhook_server.filter(serverId => isValidUUID(serverId));
      const submissionData = {
        ...formData,
        webhook_server: filteredWebhookServers
      };
      let response;
      if (configId) {
        response = await adminApi.put(`/admin/callback/system/core/manage/configurations/${configId}/`, submissionData);
      } else {
        response = await adminApi.post('/admin/callback/system/core/manage/configurations/', submissionData);
      }
      if (response.data && response.data.success) {
        const savedConfig = response.data.data;
        const eventNameForMessage = savedConfig.event_name || formData.event_name;
        const configIdForMessage = savedConfig.id || configId;
        const successMessage = `Configuration ID ${configIdForMessage} for ${eventNameForMessage} saved successfully!`;
        setSuccess(successMessage);
        onSaveSuccess(successMessage); // Pass the message to the parent
      } else {
        setError(response.data?.message || 'Failed to save configuration.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while saving configuration.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaultConfig = async () => {
    if (!eventId && !eventName) {
      setError('Event information is required to create a default configuration');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Use eventId if available, otherwise use event_name
      const payload = eventId ? { id: eventId } : { event_name: eventName };      
      const response = await adminApi.post('/admin/callback/system/core/manage/configurations/', payload);
      if (response.data && response.data.success) {
        const createdConfig = response.data.data;
        const eventNameForMessage = createdConfig.event_name || eventName;
        const configId = createdConfig.id;
        const successMessage = `Configuration ID ${configId} created successfully for ${eventNameForMessage}`;
        setSuccess(successMessage);
        // Update form with the created configuration data
        setFormData({
          ...formData,
          ...createdConfig
        });
        onSaveSuccess(successMessage);
      } else {
        setError(response.data?.message || 'Failed to create default configuration.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while creating default configuration.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDefaultData = async () => {
    if (!eventId && !eventName) {
      setError('Event information is required to generate default data');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Use eventId if available, otherwise use event_name, and add generate_only flag
      const payload = {
        ...(eventId ? { id: eventId } : { event_name: eventName }),
        generate_only: true
      };
      
      const response = await adminApi.post('/admin/callback/system/core/manage/configurations/', payload);
      
      if (response.data && response.data.success) {
        const generatedData = response.data.data;
        
        // Populate form with generated data
        setFormData(prevData => ({
          ...prevData,
          event_name: generatedData.event_name,
          description: generatedData.description,
          payload_template: generatedData.payload_template,
          message_template: generatedData.message_template,
          version: generatedData.version,
          priority: generatedData.priority,
          is_active: generatedData.is_active !== undefined ? generatedData.is_active : true,
          webhook_server: generatedData.webhook_server || prevData.webhook_server
        }));
        
        // Set selected event for context variables display
        if (generatedData.event_name && events.length > 0) {
          const event = events.find(e => e.event_name === generatedData.event_name);
          setSelectedEvent(event);
        }
        
        setSuccess('Default data generated successfully! Review and modify as needed before saving.');
        setDefaultDataGenerated(true);
      } else {
        setError(response.data?.message || 'Failed to generate default data.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while generating default data.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && configId) {
    return <div className="text-center">Loading configuration...</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="mb-3">
        <label htmlFor="event_name" className="form-label">Event Name</label>
        {eventLoading ? (
          <p>Loading events...</p>
        ) : eventError ? (
          <p className="text-danger">Error loading events: {eventError}</p>
        ) : (
          <select
            className="form-select"
            id="event_name"
            name="event_name"
            value={formData.event_name}
            onChange={handleEventChange}
            required
            disabled={mode === 'view'}
          >
            <option value="">-- Select Event --</option>
            {/* Group events by category */}
            {Array.from(new Set(events.map(event => event.category))).map(category => (
              <optgroup label={category} key={category}>
                {events.filter(event => event.category === category).map((event, index) => (
                  <option key={event.id || `${event.category}-${event.event_name}-${index}`} value={event.event_name}>
                    {event.event_name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
      </div>

      {/* Webhook Servers Selection */}
      <div className="mb-3">
        <label htmlFor="webhook_server" className="form-label">Webhook Servers</label>
        {webhookServersLoading ? (
          <p>Loading webhook servers...</p>
        ) : (
          <select
            className="form-select"
            id="webhook_server"
            name="webhook_server"
            value={formData.webhook_server}
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
              setFormData(prevData => ({
                ...prevData,
                webhook_server: selectedOptions
              }));
            }}
            multiple
            size="4"
            disabled={mode === 'view'}
            required
          >
            {webhookServers.map(server => (
              <option key={server.id} value={server.id}>
                {server.core_server_webhook_url}
                {server.description && ` (${server.description})`}
                {server.source === 'settings_py' && ' [settings.py]'}
              </option>
            ))}
          </select>
        )}
        <div className="form-text">
          Hold Ctrl/Cmd to select multiple webhook servers. All active servers are selected by default.
        </div>
      </div>
      {mode === 'new' && (eventId || eventName) && !defaultDataGenerated && (
        <div className='mb-3'>
          <button
            type="button"
            className="btn btn-outline-primary me-2 mb-2"
            onClick={handleGenerateDefaultData}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate Default Data'}
          </button>
          <button
            type="button"
            className="btn btn-primary me-2 mb-2"
            onClick={handleCreateDefaultConfig}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create & Save Default Configuration'}
          </button>
          <div className='text-sm text-secondary'>
            <strong>Generate Default Data:</strong> Populates form fields with default values for review before saving.<br/>
            <strong>Create & Save:</strong> Creates and saves a default configuration immediately. Warning! this may remove all existing configurations.
          </div>
        </div>
      )}
      <div className="mb-3">
        <label htmlFor="version" className="form-label">Version</label>
        <input
          type="text"
          className="form-control"
          id="version"
          name="version"
          value={formData.version}
          onChange={handleChange}
          required
          readOnly={mode === 'view'}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="description" className="form-label">Description</label>
        <textarea
          className="form-control"
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          readOnly={mode === 'view'}
        ></textarea>
      </div>

      <div className="mb-3">
        <label htmlFor="payload_template" className="form-label">Payload Template (JSON)</label>
        <textarea
          className="form-control"
          id="payload_template"
          name="payload_template"
          value={formData.payload_template}
          onChange={handleChange}
          rows="8"
          required
          readOnly={mode === 'view'}
          placeholder='{"event": "{{event_name}}", "timestamp": "{{timestamp}}", "data": {...}}'
        ></textarea>
        <div className="form-text">JSON template with dynamic variables using {'{'}{'{'} variable_name {'}'}{'}'}  syntax</div>
        {selectedEvent && selectedEvent.context_variables && Object.keys(selectedEvent.context_variables).length > 0 && (
          <div className="card mt-2">
            <div className="card-header">Available Variables for {selectedEvent.event_name}</div>
            <ul className="list-group list-group-flush">
              {Object.values(selectedEvent.context_variables).map(variable => (
                <li key={variable.name} className="list-group-item">
                  <code>{`{{ ${variable.name} }}`}</code>: <span className="badge bg-info">{variable.type}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mb-3">
        <label htmlFor="message_template" className="form-label">Message Template (HTML)</label>
        <textarea
          className="form-control"
          id="message_template"
          name="message_template"
          value={formData.message_template}
          onChange={handleChange}
          rows="5"
          readOnly={mode === 'view'}
          placeholder='<p>Event {{event_name}} occurred at {{timestamp}}</p>'
        ></textarea>
        <div className="form-text">HTML template with dynamic variables using {'{'}{'{'} variable_name {'}'}{'}'}  syntax</div>
      </div>

      <div className="mb-3">
        <label htmlFor="priority" className="form-label">Priority</label>
        <input
          type="number"
          className="form-control"
          id="priority"
          name="priority"
          value={formData.priority}
          onChange={handleChange}
          required
          readOnly={mode === 'view'}
        />
      </div>

      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="is_active"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          disabled={mode === 'view'}
        />
        <label className="form-check-label" htmlFor="is_active">Is Active</label>
      </div>

      {/* Organizational Scoping */}
      <h5 className="mt-4">Organizational Scoping (Optional)</h5>
      <div className="mb-3">
        <label htmlFor="health_system" className="form-label">Health System</label>
        <select
          className="form-select"
          id="health_system"
          name="health_system"
          value={formData.health_system}
          onChange={handleChange}
          disabled={mode === 'view'}
        >
          <option value="">-- Select Health System --</option>
          {healthSystems.map(hs => (
            <option key={hs.id} value={hs.id}>{hs.name}</option>
          ))}
        </select>
      </div>

      {formData.health_system && (
        <>
          <div className="mb-3">
            <label htmlFor="department" className="form-label">Department</label>
            <select
              className="form-select"
              id="department"
              name="department"
              value={formData.department}
              onChange={handleChange}
              disabled={mode === 'view'}
            >
              <option value="">-- Select Department --</option>
              {departments.map(dep => (
                <option key={dep.id} value={dep.id}>{dep.name}</option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label htmlFor="clinic" className="form-label">Clinic</label>
            <select
              className="form-select"
              id="clinic"
              name="clinic"
              value={formData.clinic}
              onChange={handleChange}
              disabled={mode === 'view'}  
            >
              <option value="">-- Select Clinic --</option>
              {clinics.map(clinic => (
                <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label htmlFor="provider" className="form-label">Provider</label>
            <select
              className="form-select"
              id="provider"
              name="provider"
              value={formData.provider}
              onChange={handleChange}
              disabled={mode === 'view'}
            >
              <option value="">-- Select Provider --</option>
              {providers.map(prov => (
                <option key={prov.id} value={prov.id}>{prov.first_name} {prov.last_name}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {mode !== 'view' && (
        <div className="d-flex flex-column gap-2">
          <div className="d-flex">
            <button type="submit" className="btn btn-primary me-2" disabled={loading}>
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      )}
    </form>
  );
};

export default CallbackConfigurationForm;